/**
 * @fileoverview `readJsonBody(response)` — login-shell's ONE reading of a fetch response body.
 * @module @puffin-core/login-shell/utils/readJsonBody
 *
 * login-shell's four raw-fetch callers (the pre-auth JSON poster, the password-reset client, and
 * the authenticator's status poll and TOTP submit) each read the body with
 * `response.json().catch(() => ({}))`. That caught EVERYTHING, so a defect in reading the body (a
 * body already consumed, a non-Response handed in) read exactly like a body that was not JSON
 * (SWALLOWED-CATCH-BURNDOWN-001 U3; the four copies converge here).
 *
 * The ONE expected failure is a body that is not JSON (an empty 204, an HTML error page from a
 * proxy): `Response.json()` rejects with a `SyntaxError` (Fetch Standard, "consume body" → "parse
 * JSON from bytes"). That answers `null` — "the body said nothing readable" — and each caller then
 * refuses on its own terms (a translated generic refusal, or "incomplete"). Anything else rejects.
 *
 * @param {Response} response
 * @returns {Promise<(*|null)>} the parsed body, or `null` when the body is not JSON
 * @throws whatever is not a `SyntaxError`
 */
export async function readJsonBody(response) {
  try {
    return await response.json();
  } catch (error) {
    // By NAME, not `instanceof`: the body parser may live in another realm (a test runtime's fetch, an
    // iframe's Response), where its SyntaxError is not this realm's — measured, Node's `Response`
    // under jest fails `instanceof SyntaxError`.
    if (error?.name !== 'SyntaxError') throw error;
    return null;
  }
}
